import {
  MENU_ICON_SLOT_CLASSES,
  MENU_ITEM_CLASSES,
  MENU_SEPARATOR_CLASSES,
  useDrillFocus,
} from "@controls";
import { ChevronRight, Filter, ListFilter } from "@icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import BooleanPanel from "./boolean-panel";
import ComparisonPanel from "./comparison-panel";
import { DATE_OPERATOR_KEYS, NUMERIC_OPERATOR_KEYS } from "./constants";
import TextOptionsPanel from "./text-options-panel";
import {
  ComparisonFilterValue,
  CompoundFilterGroup,
  FilterMenuItem,
  LogicalOperator,
} from "./types";

interface FilterMenuContentProps {
  filterMenuItemGroups: FilterMenuItem[][];
  selectedValues: Record<string, string[]>;
  comparisonValues: Record<string, ComparisonFilterValue>;
  booleanValues: Record<string, string>;
  onSelectionChange: (name: string, values: string[]) => void;
  onComparisonChange: (name: string, value: ComparisonFilterValue) => void;
  onBooleanChange: (name: string, value: string) => void;
  onCreateCompoundGroup?: (group: CompoundFilterGroup) => void;
  existingCompoundLogics?: LogicalOperator[];
}

export default function FilterMenuContent({
  filterMenuItemGroups,
  selectedValues,
  comparisonValues,
  booleanValues,
  onSelectionChange,
  onComparisonChange,
  onBooleanChange,
  onCreateCompoundGroup,
  existingCompoundLogics = [],
}: FilterMenuContentProps) {
  const { t } = useTranslation();
  const [activeMenu, setActiveMenu] = useState<FilterMenuItem | null>(null);
  const contentRef = useDrillFocus(activeMenu ? [activeMenu.name] : []);

  function renderPanel(menuItem: FilterMenuItem) {
    if (menuItem.type === "TEXT") {
      return (
        <TextOptionsPanel
          menuItem={menuItem}
          selected={selectedValues[menuItem.name] ?? []}
          onSelectionChange={(values) =>
            onSelectionChange(menuItem.name, values)
          }
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    if (menuItem.type === "BOOLEAN") {
      return (
        <BooleanPanel
          menuItem={menuItem}
          value={booleanValues[menuItem.name]}
          onChange={(value) => onBooleanChange(menuItem.name, value)}
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    if (menuItem.type === "DATE") {
      return (
        <ComparisonPanel
          menuItem={menuItem}
          inputType="date"
          operatorKeys={DATE_OPERATOR_KEYS}
          value={comparisonValues[menuItem.name]}
          onChange={(value) => onComparisonChange(menuItem.name, value)}
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    return (
      <ComparisonPanel
        menuItem={menuItem}
        inputType="number"
        operatorKeys={NUMERIC_OPERATOR_KEYS}
        value={comparisonValues[menuItem.name]}
        onChange={(value) => onComparisonChange(menuItem.name, value)}
        onBack={() => setActiveMenu(null)}
      />
    );
  }

  const hasAnd = existingCompoundLogics.includes("and");
  const hasOr = existingCompoundLogics.includes("or");
  const canAddCompound = onCreateCompoundGroup && (!hasAnd || !hasOr);

  function createGroup(logic: LogicalOperator) {
    onCreateCompoundGroup?.({
      id: `compound-${logic}`,
      logic,
      conditions: [],
    });
  }

  function renderRootItems() {
    return (
      <>
        {canAddCompound && (
          <>
            {!hasAnd && (
              <DropdownMenu.Item
                onSelect={() => createGroup("and")}
                className={MENU_ITEM_CLASSES}
              >
                <span className={MENU_ICON_SLOT_CLASSES}>
                  <Filter size={16} />
                </span>
                <span className="flex-1 min-w-0">{t("compoundFilterAnd")}</span>
              </DropdownMenu.Item>
            )}
            {!hasOr && (
              <DropdownMenu.Item
                onSelect={() => createGroup("or")}
                className={MENU_ITEM_CLASSES}
              >
                <span className={MENU_ICON_SLOT_CLASSES}>
                  <ListFilter size={16} />
                </span>
                <span className="flex-1 min-w-0">{t("compoundFilterOr")}</span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator className={MENU_SEPARATOR_CLASSES} />
          </>
        )}
        {filterMenuItemGroups.map((group, groupIndex) => (
          <DropdownMenu.Group key={groupIndex}>
            {groupIndex > 0 && (
              <DropdownMenu.Separator className={MENU_SEPARATOR_CLASSES} />
            )}
            {group.map((menuItem) => (
              <DropdownMenu.Item
                key={menuItem.name}
                data-menu-key={menuItem.name}
                onSelect={(e) => {
                  e.preventDefault();
                  setActiveMenu(menuItem);
                }}
                className={MENU_ITEM_CLASSES}
              >
                <span className={MENU_ICON_SLOT_CLASSES}>{menuItem.icon}</span>
                <span className="flex-1 min-w-0">{menuItem.label}</span>
                <span className="ml-auto shrink-0 text-muted transition-colors duration-100 group-data-[highlighted]:text-primary">
                  <ChevronRight size={16} />
                </span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Group>
        ))}
      </>
    );
  }

  return (
    <div ref={contentRef}>
      {activeMenu ? renderPanel(activeMenu) : renderRootItems()}
    </div>
  );
}
